<template>
  <div
    class="app"
    :class="`current-scene-${currentScene}`"
  >
    <div class="stage">
      <div class="stage__padding">
        <div class="stage__inner">
          <button
            class="scene__nav scene__nav--previous button"
            v-if="currentScene > 1 && currentScene!=8"
            @click="previousScene()"
          >
            <span class="scene__nav-inner">Back</span>
          </button>
          <button
            class="scene__nav scene__nav--next button"
            @click="nextScene()"
            v-if="currentScene !=8"
          >
            <span class="scene__nav-inner">{{ nextButtonText }}</span>
          </button>
          <button
            class="mute"
            :class="{
              'muted': muted
            }"
            @click="toggleMute()"
          >toggle mute</button>
          <div
            class="scene scene-1"
            :class="{
              'active': currentScene === 1,
              'active-play': currentScene === 1
            }"
          >
            <h1 class="scene__title">How Do You Make a Cast?</h1>
            <div class="scene-1__head"></div>
            <div class="scene__text">
              <p class="scene__text__headline">Casting a Head</p>
              <p class="scene__text__description">Casting is the process of changing a sculpture made from a soft, temporary substance, like unfired clay or wax, to a hard material such as cement, bronze or steel.  The artist makes a mold from the original artwork and uses the mold to cast a permanent sculpture.</p>
            </div>
          </div>
          <div
            class="scene scene-2"
            :class="{
              'active': currentScene === 2
            }"
          >
            <p class="scene__title">How Do You Make a Cast?</p>
            <Container group-name="dragMold" @drop="toolDrop('click')" :should-animate-drop="()=>false" class="interactive-box">
              <div 
                class="head"
                :class="{
                  'head_on_post': !hasToolBeenUsed,
                  'head_mold': hasToolBeenUsed
                }"
              ></div>
            </Container>
            <div class="scene__text">
              <p class="scene__text__headline">Make a Mold Joint</p>
              <p class="scene__text__description">Divide the clay head into two parts by dragging the mold joint to the top of the sculpture.</p>
            </div>
            <div class="tool-box">
              <p>Mold Joint</p>
              <div class="tool-box__area">
                <Container group-name="dragMold" :drag-begin-delay="0">
                  <Draggable v-if="!hasToolBeenUsed" class="tool">
                    <div class="tool__mold"></div>
                  </Draggable>
                </Container>
              </div>
            </div>
          </div>
          <div
            class="scene scene-3"
            :class="{
              'active': currentScene === 3
            }"
          >
            <p class="scene__title">How Do You Make a Cast?</p>
            <Container group-name="dragPlaster" @drop="toolDrop('blob')" :should-animate-drop="()=>false" class="interactive-box">
              <div 
                class="head"
                :class="{
                  'head_mold': !hasToolBeenUsed,
                  'head_plaster': hasToolBeenUsed
                }"
              ></div>
            </Container>
            <div class="scene__text">
              <p class="scene__text__headline">Add Plaster</p>
              <p class="scene__text__description">Drag the plaster over the face to cover both sides of the head, leaving a little bit of the mold joint sticking out.</p>
            </div>
            <div class="tool-box">
              <p>Plaster</p>
              <div class="tool-box__area">
                <Container group-name="dragPlaster" :drag-begin-delay="0">
                  <Draggable v-if="!hasToolBeenUsed" class="tool">
                    <div class="tool__plaster"></div>
                  </Draggable>
                </Container>
              </div>
            </div>
          </div>
          <div
            class="scene scene-4"
            :class="{
              'active': currentScene === 4
            }"
          >
            <p class="scene__title">How Do You Make a Cast?</p>
            <div 
              class="chisel-animate"
              :class="{
                'active': hasToolBeenUsed && trackTime < 2,
                'animate': hasToolBeenUsed && ((trackTime > .3 && trackTime < .5) || (trackTime > .8 && trackTime < 1.1) || (trackTime > 1.4 && trackTime <1.7))
              }"
            ></div>
            <div 
              class="mallet-animate"
              :class="{
                'active': hasToolBeenUsed && trackTime < 2,
                'animate': hasToolBeenUsed && ((trackTime > .3 && trackTime < .5) || (trackTime > .8 && trackTime < 1.1) || (trackTime > 1.4 && trackTime <1.7))
              }"
            ></div>
            <Container group-name="dragChisel" @drop="toolDrop('chisel')" :should-animate-drop="()=>false" class="interactive-box">
              <div 
                class="head"
                :class="{
                  'head_split': !hasToolBeenUsed || (hasToolBeenUsed && trackTime < 2),
                  'head_open': hasToolBeenUsed && trackTime >= 2
                }"
              ></div>
            </Container>
            <div class="scene__text">
              <p class="scene__text__headline">Separate the Halves</p>
              <p class="scene__text__description">The plaster dries for at least eight hours.  Drag and release the chisel and mallet over the mold to separate the two halves.</p>
            </div>
            <div class="tool-box chisel">
              <p>Chisel & Mallet</p>
              <div class="tool-box__area">
                <Container group-name="dragChisel" :drag-begin-delay="0">
                  <Draggable v-if="!hasToolBeenUsed" class="tool">
                    <div class="tool__chisel"></div>
                  </Draggable>
                </Container>
              </div>
            </div>
          </div>
          <div
            class="scene scene-5"
            :class="{
              'active': currentScene === 5
            }"
          >
            <p class="scene__title">How Do You Make a Cast?</p>
            <Container group-name="dragString" @drop="toolDrop('string')" :should-animate-drop="()=>false" class="interactive-box">
              <div 
                class="head"
                :class="{
                  'head_separate': !hasToolBeenUsed || (hasToolBeenUsed && trackTime < .3),
                  'head_no_string': hasToolBeenUsed && trackTime >= .3 && trackTime < .7,
                  'head_string_1': hasToolBeenUsed && trackTime >= .7 && trackTime < 1.2,
                  'head_string_2': hasToolBeenUsed && trackTime >= 1.2 && trackTime < 1.6,
                  'head_string_3': hasToolBeenUsed && trackTime >= 1.6 && trackTime < 2,
                  'head_string_4': hasToolBeenUsed && trackTime >= 2
                }"
              ></div>
            </Container>
            <div class="scene__text">
              <p class="scene__text__headline">Put Halves Back Together</p>
              <p class="scene__text__description">The inside of the mold is the negative image of the modeled head.  Drag and release the string to put the two halves of the mold back together and tie them.</p>
            </div>
            <div class="tool-box string">
              <p>String</p>
              <div class="tool-box__area">
                <Container group-name="dragString" :drag-begin-delay="0">
                  <Draggable v-if="!hasToolBeenUsed" class="tool">
                    <div class="tool__string"></div>
                  </Draggable>
                </Container>
              </div>
            </div>
          </div>
          <div
            class="scene scene-6"
            :class="{
              'active': currentScene === 6
            }"
          >
            <p class="scene__title">How Do You Make a Cast?</p>
            <div 
              class="pour-cup"
              :class="{
                'active': hasToolBeenUsed && trackTime < 2,
                'pour-cup__1': hasToolBeenUsed && trackTime < .6,
                'pour-cup__2': hasToolBeenUsed && trackTime >= .6 && trackTime < 1.2,
                'pour-cup__3': hasToolBeenUsed && trackTime >= 1.2 && trackTime < 1.6,
                'pour-cup__4': hasToolBeenUsed && trackTime >= 1.6
              }"
            ></div>
            <Container group-name="dragLiquid" @drop="toolDrop('liquid')" :should-animate-drop="()=>false" class="interactive-box">
              <div 
                class="head"
                :class="{
                  'head_string_4': !hasToolBeenUsed || (hasToolBeenUsed && trackTime < 1.6),
                  'head_string_filled': hasToolBeenUsed && trackTime >= 1.6
                }"
              ></div>
            </Container>
            <div class="scene__text">
              <p class="scene__text__headline">Pour in the Liquid</p>
              <p class="scene__text__description">Now the mold is upside down with the hole left by the armature at the top.  Drag and release the liquid until it fills the mold.  It could be cement, plastic, or another hardening substance.</p>
            </div>
            <div class="tool-box liquid">
              <p>Liquid Cement</p>
              <div class="tool-box__area">
                <Container group-name="dragLiquid" :drag-begin-delay="0">
                  <Draggable v-if="!hasToolBeenUsed" class="tool">
                    <div class="tool__liquid"></div>
                  </Draggable>
                </Container>
              </div>
            </div>
          </div>
          <div
            class="scene scene-7"
            :class="{
              'active': currentScene === 7
            }"
          >
            <p class="scene__title">How Do You Make a Cast?</p>
            <div 
              class="chisel-animate scene-7"
              :class="{
                'active': hasToolBeenUsed && trackTime < 2,
                'animate': hasToolBeenUsed && ((trackTime > .3 && trackTime < .5) || (trackTime > .8 && trackTime < 1.1) || (trackTime > 1.4 && trackTime <1.7))
              }"
            ></div>
            <div 
              class="mallet-animate scene-7"
              :class="{
                'active': hasToolBeenUsed && trackTime < 2,
                'animate': hasToolBeenUsed && ((trackTime > .3 && trackTime < .5) || (trackTime > .8 && trackTime < 1.1) || (trackTime > 1.4 && trackTime <1.7))
              }"
            ></div>
            <Container group-name="dragChisel2" @drop="toolDrop('chisel')" :should-animate-drop="()=>false" class="interactive-box">
              <div 
                class="head"
                :class="{
                  'head_filled': !hasToolBeenUsed || (hasToolBeenUsed && trackTime < 2),
                  'head_done': hasToolBeenUsed && trackTime >= 2
                }"
              ></div>
            </Container>
            <div class="scene__text">
              <p class="scene__text__headline">Separate Again</p>
              <p class="scene__text__description">The liquid inside the mold hardens over several days.  Drag and release the hammer and chisel to separate the two halves of the plaster mold again, revealing the cast head inside.</p>
            </div>
            <div class="tool-box chisel scene7">
              <p>Chisel & Mallet</p>
              <div class="tool-box__area">
                <Container group-name="dragChisel2" :drag-begin-delay="0">
                  <Draggable v-if="!hasToolBeenUsed" class="tool">
                    <div class="tool__chisel"></div>
                  </Draggable>
                </Container>
              </div>
            </div>
          </div>
          <div
            class="scene scene-8"
            :class="{
              'active': currentScene === 8
            }"
          >
            <p class="scene__title">How Do You Make a Cast?</p>
            <div class="scene__text">
              <p class="scene__text__headline">Finally</p>
              <p class="scene__text__description">Now you have a sculpture of a head in a material that will last for many years.</p>
              <a class="scene__text__nav" href="https://www.knowitall.org/series/sculpture-studio">Back to Studio</a>
            </div>
            <div class="scene-8__head"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import { Container, Draggable } from 'vue-smooth-dnd';

export default {
  name: 'app',
  components: {
    Container,
    Draggable
  },
  computed: {
    ...mapState([
      'numScenes',
      'currentScene',
      'beginButton',
      'muted',
      'hasToolBeenUsed',
      'trackTime'
    ]),
    nextButtonText () {
      if (this.currentScene < this.numScenes) {
        return 'Next'
      } else {
        return 'End'
      }
    }
  },
  methods: {
    ...mapMutations([
      'HIDE_BEGIN_BUTTON',
      'RESET_TRACK_INTERVAL'
    ]),
    ...mapActions([
      'begin',
      'previousScene',
      'nextScene',
      'toggleMute',
      'toolDrop'
    ]),
  }
}
</script>

<style lang="scss">
body {
  background-color: #FFF;
}
</style>
